import type { EncodedID } from '@compassdigital/id/interface';
import type { Address } from '../address';
import type {
  ConfigTender,
  PaymentConfig,
  SoftwareIntegrations,
  StationPrivateConfig,
  StationPublicConfig,
  TenderTypes,
} from '../cd-config';
import type { Integrations } from '../integration';
import type { MenuMeta, V2MenuHours } from '../menu';
import type { RecordEntityId } from '../records';
import type { DeviceMapping } from '../site';
import type { StationMealplanConfiguration } from '@centric-os/types';

// Station Order Methods
export enum OrderMethods {
  PRE_ORDER = 'pre-order',
  SCAN_N_PAY = 'scan-n-pay',
  JWO = 'just-walk-out',
}

// Scan & Pay Menu Types
export enum ScanNPayMenuType {
  Centric = 'centric',
  Canteen = 'canteen',
}

export interface CentricStationIs {
  pickup_supported: boolean;
  delivery_supported: boolean;
  scan_and_go_supported?: boolean;
  frictionless_supported?: boolean;
}

export interface StationPickupConfig {
  averagePrepTime: number; // in minutes
  customersPerSlot: number; // number of orders per slot
  timeslotLength: number; // in minutes
  isGuestCheckin?: boolean;
}

export enum DropoffType {
  STAFF = 'staff',
  ROBOT = 'robot',
}

export interface StationDropoffConfig {
  // Automatic drop-off configuration
  autoTime?: number; // in minutes
  destinations?: string[]; // list of locations

  // User-defined drop-off configuration
  customTimeslots?: CustomDropoffConfig[];

  // Robot drop-off configuration
  dropoffType?: DropoffType;

  // Common configuration
  customersPerSlot?: number; // number of orders per slot
  isCustom: boolean;
  prepTime?: number; // in minutes
  showSingleTimeslot?: boolean;
  showInstructions?: boolean;
  runnerAppEnabled?: boolean;
  deliveryFee?: number; // in dollars
}

export interface CustomDropoffConfig {
  startTime: Date;
  endTime: Date;
  destinations: string[]; // list of locations
}

export interface StationCentricFinancialConfig {
  /**
   * Tax rate as a fraction of 1. For example, 0.05 for 5% tax
   * Will be input as a percentage in the UI and then converted to a fraction in the payload
   */
  taxRate?: number;
  /**
   * Service fee applied to orders, as a dollar value
   */
  serviceFee?: number;
  jdeCategory?: string;
}

export interface StationOtherConfig {
  orderNotificationEmail?: string;
  menuDisclaimer?: MenuDisclaimer;
  specialInstructionsEnabled?: boolean;
  diningHallEnabled?: boolean;
}

export interface MenuDisclaimer {
  en?: string;
  fr?: string;
}

export interface PaymentConfiguration {
  paymentSettings: Pick<PaymentConfig, 'type' | 'freedompay' | 'is_encrypted' | 'data'>;
  mealPlanConfig?: {
    type: string;
  };
}

export interface FrictionlessConfig {
  frictionless_supported: Is['frictionless_supported'];
  frictionless_third_party_partner: StationBrandMeta['frictionless_third_party_partner'];
  jwo: SoftwareIntegrations['jwo'];
}

export interface OptionalStationCentricConfig<T extends StationMealplanConfiguration> {
  orderMethod?: OrderMethods;
  is?: CentricStationIs;
  pickupConfig?: StationPickupConfig;
  dropoffConfig?: StationDropoffConfig;
  financialConfig?: StationCentricFinancialConfig;
  otherConfig?: StationOtherConfig;
  paymentConfig?: PaymentConfiguration;
  frictionlessConfig?: FrictionlessConfig;
  mealplanConfig?: T;
  paymentExclusions?: PaymentExclusions;
}

export interface StationCentricConfig<
  T extends StationMealplanConfiguration = StationMealplanConfiguration,
> extends OptionalStationCentricConfig<T> {
  // Additional required fields or constraints can be added here later.
  id: string;
}

// Translate from BE - GetStationResponse
export interface StationOverview {
  id: string;
  name: string;
  stationLocation: string;
  businessUnit: string;
  integrations: Integrations;
}

// Interface to facade full station payload
export interface StationAggregatePayload {
  station: Partial<StationBrand>;
  stationPublicConfig: Partial<StationPublicConfig>;
  stationPrivateConfig: Partial<StationPrivateConfig>;
}

export interface StationBrand {
  [key: string]: any;
  id: string;
  address?: Address;
  brand?: string;
  deliveryHours?: Hours[];
  estimated_wait_time?: StationBrandWaitTime;
  group: string;
  hours?: Hours[];
  sector?: string;
  name?: string;
  label?: StationBrandLabel;
  timezone?: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  menus?: V2MenuHours[];
  date?: {
    deleted?: string;
    created?: string;
    modified?: string;
    next_calendar_sync?: string;
  };
  style?: any;
  pos?: string;
  terminals?: any[];
  device_mapping?: DeviceMapping;
  timeslots?: Timeslot;
  is?: Is;
  location?: string;
  payment_provider?: string;
  location_description?: string;
  company?: string;
  config?: {
    private?: any;
    public?: any;
  };
  tax_rate?: number;
  meta?: StationBrandMeta;
  descriptions?: {
    location?: {
      en?: string;
      fr?: string;
    };
  };
  color?: {
    enabledColor: string;
    disabledColor: string;
  };
  sequence?: number;
  channels?: string[];
}

interface StationBrandMeta {
  scout?: {
    user_id: string;
    name: {
      first: string;
      last: string;
    };
  };
  partner?: {
    user_id: string;
    name: {
      first: string;
      last: string;
    };
  };
  contact?: {
    name: string;
    role: string;
    email: string;
    phone: string;
  };
  market_id?: string;
  partner_id?: string;
  business_number?: number;
  website?: string;
  cuisine_types?: string[];
  status?: string[];
  active?: boolean;
  max_showcase_items?: number;
  frictionless_store_uuid?: string;
  menu?: MenuMeta;
  /** @deprecated use software_integrations */
  type_of_kds?: Record<string, boolean>;
  has_kds?: boolean;
  software_integrations?: SoftwareIntegrations;
  frictionless_third_party_partner?: string;
  jde_category?: string;
}

export interface Is {
  pickup_supported?: boolean;
  delivery_supported?: boolean;
  plu_enabled?: boolean;
  promo_exemptions_enabled?: boolean;
  local_images_enabled?: boolean;
  hidden?: boolean;
  show_in_kiosk?: boolean;
  show_in_pos?: boolean;
  web_order_enabled?: boolean;
  item_desc_edit_enabled?: boolean;
  scan_and_go_supported?: boolean;
  frictionless_supported?: boolean;
  calories_edit_enabled?: boolean;
  cafe360?: boolean;
  special_instructions_enabled?: boolean;
  dining_hall?: boolean;
  [index: string]: any;
}

export interface Hours {
  id?: string;
  date?: {
    deleted?: string;
    start?: string;
    end?: string;
  };
  day?: {
    start?: number;
    end?: number;
  };
  hours?: string;
}

export interface MenuHours {
  id?: string;
  date?: {
    start?: string;
    end?: string;
  };
  hours?: string;
}

export interface StationBrandWaitTime {
  [key: string]: {
    max: number;
    min: number;
    ready_time: number;
  };
}

export interface Timeslot {
  time?: string;
  duration_minutes?: number;
  customers_per_slot?: number;
  menu_items_per_slot?: number;
  averagePrepTime?: string;
  delivery_time?: string;
  delivery_customers_per_slot?: number;
  delivery_menu_items_per_slot?: number;
  delivery_is_user_defined?: boolean;
  delivery_user_defined?: DeliveryUserDefined[];
  delivery_prep_time?: string;
  delivery_destinations?: string[];
}

export interface TimeRange {
  start: number;
  end: number;
}

export interface DeliveryUserDefined {
  start_time: string | Date;
  end_time: string | Date;
  delivery_destinations: string[];
}
export type CustomDropoffTimeslotConfig = {
  start_time: Date;
  end_time: Date;
  destinations: string[];
};

export interface StationBrandLabel {
  en: string;
  fr?: string;
}

export interface DeliveryDestination {
  address: DeliveryDestinationAddress;
  foodlocker: boolean;
  name: string;
  id?: RecordEntityId;
  information?: string;
  sort_sequence?: number;
}

export interface DeliveryDestinationDelete {
  delivery_destination_ids: RecordEntityId[];
}

export interface DeliveryDestinationRequest {
  delivery_destinations: DeliveryDestination[];
}

export interface DeliveryDestinationResponse {
  delivery_destinations: DeliveryDestination[];
}

export interface DeliveryDestinationAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  coordinates: { latitude: number; longitude: number };
}

export interface StationBrandConfig {
  brands: StationConfig[];
}

export interface StationConfig {
  id: RecordEntityId;
  is: Is;
}

export interface PaymentCards {
  creditCard: boolean;
  applePay: boolean;
  googleWallet: boolean;
}

export interface PaymentExclusions {
  paymentCards?: PaymentCards;
  cashless?: string[];
  mealplans?: ConfigTender[];
}
